import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'

import { creativeAiApi, type CreativeAiScope } from '../api/CreativeAiApi'
import type { CreativeLabel, LabelSuggestion, SuggestMeta } from '../models/labelEditor.types'
import type { CreativeLabellingProgressEvent, LabellingStatus } from '../models/labelling.types'
import { LABELLING_ACTIVE_STATUSES } from '../models/labelling.types'
import type {
  LabelSchemaConstraints,
  LabelSchemaResponse,
  LabelSchemaRunSummary,
  LabelSchemaWriteResponse,
  LabelSuggestionRequest,
} from '../models/labelSchema.types'
import {
  diffSchema,
  draftToWriteSchemas,
  labelErrorsFromPayload,
  parseSuggestions,
  schemaToDraft,
  suggestionToLabel,
} from '../service/labelEditor'

import { ApiError } from '@/shared/http'
import type { UiAsyncState } from '@/shared/ui'

const ACTIVE = new Set<string>(LABELLING_ACTIVE_STATUSES)
const FALLBACK_CONSTRAINTS: LabelSchemaConstraints = {
  max_labels_per_schema: 25,
  max_labels_per_scope: { image: 25, video: 25 },
  multiple_min_values: 2,
  reserved_key_prefix: '_',
  key_pattern: '^[a-z][a-z0-9_]*$',
}

function apiError(cause: unknown): ApiError {
  return ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function sameScope(left: CreativeAiScope | null, right: CreativeAiScope): boolean {
  return Boolean(
    left &&
    String(left.accountId) === String(right.accountId) &&
    String(left.workspaceId) === String(right.workspaceId),
  )
}

export const useLabelSchemaStore = defineStore('creativeAiLabelSchema', () => {
  const schema = shallowRef<LabelSchemaResponse | null>(null)
  const constraints = ref<LabelSchemaConstraints>(clone(FALLBACK_CONSTRAINTS))
  const schemaState = ref<UiAsyncState>('empty')
  const schemaError = shallowRef<ApiError | null>(null)
  const draft = ref<CreativeLabel[]>([])
  const original = ref<CreativeLabel[]>([])
  const labelErrors = ref<Record<string, string>>({})
  const saveState = ref<UiAsyncState>('empty')
  const saveError = shallowRef<ApiError | null>(null)
  const lastSaveQueued = ref(false)
  const suggestions = ref<LabelSuggestion[]>([])
  const suggestMeta = ref<SuggestMeta | null>(null)
  const suggestState = ref<UiAsyncState>('empty')
  const suggestError = shallowRef<ApiError | null>(null)
  const activeRun = shallowRef<LabelSchemaRunSummary | null>(null)
  const runState = ref<UiAsyncState>('empty')
  const runError = shallowRef<ApiError | null>(null)
  let currentScope: CreativeAiScope | null = null
  let schemaRequest: Promise<void> | null = null
  let generation = 0
  let nextLocalId = 1
  let terminalTimer: ReturnType<typeof setTimeout> | null = null

  const diff = computed(() => diffSchema(draft.value, original.value))
  const isDirty = computed(() => diff.value.count > 0)
  const hasActiveRun = computed(() =>
    Boolean(activeRun.value && ACTIVE.has(activeRun.value.status)),
  )
  const runPercent = computed(() => {
    const run = activeRun.value
    return run && run.total_count > 0
      ? Math.min(100, (run.processed_count / run.total_count) * 100)
      : 0
  })

  function labelsByScope(scope: 'image' | 'video'): CreativeLabel[] {
    return draft.value.filter((label) => label.scope === scope)
  }

  function labelCount(scope: 'image' | 'video'): number {
    return labelsByScope(scope).length
  }

  function isAtCap(scope: 'image' | 'video'): boolean {
    const cap =
      constraints.value.max_labels_per_scope[scope] ?? constraints.value.max_labels_per_schema
    return labelCount(scope) >= cap
  }

  function adoptSchema(response: LabelSchemaResponse): void {
    schema.value = response
    const rows = schemaToDraft(response)
    draft.value = clone(rows)
    original.value = clone(rows)
    nextLocalId = rows.reduce((max, row) => Math.max(max, row.id), 0) + 1
    labelErrors.value = {}
    schemaState.value = rows.length ? 'ready' : 'empty'
    if (response.active_run) adoptRun(response.active_run)
    else if (!activeRun.value || ACTIVE.has(activeRun.value.status)) activeRun.value = null
  }

  async function loadSchema(scope: CreativeAiScope, silent = false): Promise<void> {
    if (!sameScope(currentScope, scope)) resetState()
    if (sameScope(currentScope, scope) && schemaRequest) return schemaRequest
    if (
      sameScope(currentScope, scope) &&
      schema.value !== null &&
      (schemaState.value === 'ready' || schemaState.value === 'empty')
    )
      return
    currentScope = scope
    const request = ++generation
    if (!silent) schemaState.value = 'loading'
    schemaError.value = null
    const pending = (async () => {
      try {
        const response = await creativeAiApi.getLabelSchema(scope)
        if (request !== generation) return
        adoptSchema(response)
      } catch (cause) {
        if (request !== generation) return
        schemaError.value = apiError(cause)
        schemaState.value = 'error'
      } finally {
        if (request === generation) schemaRequest = null
      }
    })()
    schemaRequest = pending
    return pending
  }

  async function loadConstraints(scope: CreativeAiScope): Promise<void> {
    try {
      const meta = await creativeAiApi.getLabelSchemaMeta(scope)
      if (sameScope(currentScope, scope)) constraints.value = meta.constraints
    } catch {
      if (sameScope(currentScope, scope)) constraints.value = clone(FALLBACK_CONSTRAINTS)
    }
  }

  function resetDraft(): void {
    draft.value = clone(original.value)
    labelErrors.value = {}
    saveError.value = null
    saveState.value = 'empty'
  }

  function upsertLabel(label: CreativeLabel): void {
    const index = draft.value.findIndex((row) => row.id === label.id)
    if (index < 0) draft.value.push({ ...clone(label), id: nextLocalId++ })
    else draft.value[index] = clone(label)
    labelErrors.value = {}
  }

  function removeLabel(id: number): void {
    draft.value = draft.value.filter((label) => label.id !== id)
    labelErrors.value = {}
  }

  function addSuggestions(selected: readonly LabelSuggestion[]): void {
    const existing = new Set(draft.value.map((label) => `${label.scope}:${label.key}`))
    const counts = { image: labelCount('image'), video: labelCount('video') }
    for (const suggestion of selected) {
      const identity = `${suggestion.scope}:${suggestion.key}`
      const cap =
        constraints.value.max_labels_per_scope[suggestion.scope] ??
        constraints.value.max_labels_per_schema
      if (existing.has(identity) || counts[suggestion.scope] >= cap) continue
      draft.value.push(suggestionToLabel(suggestion, nextLocalId++))
      existing.add(identity)
      counts[suggestion.scope] += 1
    }
  }

  async function saveSchema(): Promise<LabelSchemaWriteResponse | null> {
    if (!currentScope || !isDirty.value) return null
    saveState.value = 'loading'
    saveError.value = null
    labelErrors.value = {}
    lastSaveQueued.value = false
    try {
      const result = await creativeAiApi.updateLabelSchema(
        currentScope,
        draftToWriteSchemas(draft.value, original.value),
      )
      original.value = clone(draft.value)
      saveState.value = 'ready'
      lastSaveQueued.value =
        result.status === 202 ||
        Boolean(
          result.data &&
          typeof result.data === 'object' &&
          'queued' in result.data &&
          result.data.queued,
        )
      return result.data
    } catch (cause) {
      const error = apiError(cause)
      if (error.kind === 'conflict' && currentScope) {
        try {
          const response = await creativeAiApi.listLabellingRuns(currentScope, { limit: 1 })
          if (response.active_run) adoptRun(response.active_run)
        } catch {
          // Preserve the PATCH conflict when the adoption read itself cannot complete.
        }
      }
      saveError.value = error
      labelErrors.value = labelErrorsFromPayload(error.payload)
      saveState.value = 'error'
      throw error
    }
  }

  async function loadSuggestions(request: LabelSuggestionRequest): Promise<void> {
    if (!currentScope) return
    suggestState.value = 'loading'
    suggestError.value = null
    try {
      const parsed = parseSuggestions(await creativeAiApi.suggestLabelSchema(currentScope, request))
      suggestions.value = parsed.suggestions
      suggestMeta.value = parsed.meta
      suggestState.value = parsed.suggestions.length ? 'ready' : 'empty'
    } catch (cause) {
      suggestError.value = apiError(cause)
      suggestState.value = 'error'
    }
  }

  function adoptRun(run: LabelSchemaRunSummary): void {
    if (terminalTimer) clearTimeout(terminalTimer)
    terminalTimer = null
    activeRun.value = clone(run)
    runState.value = 'ready'
    runError.value = null
  }

  function applyRelabelProgress(frame: CreativeLabellingProgressEvent): void {
    if (frame.run_type !== 'relabel') return
    const current = activeRun.value
    adoptRun({
      ...(current?.id === frame.id ? current : {}),
      id: frame.id,
      run_type: 'relabel',
      scope: frame.scope,
      status: frame.status as LabellingStatus,
      processed_count: frame.processed_count,
      total_count: frame.total_count,
      failed_count: frame.failed_count,
      skipped_count: frame.skipped_count,
      eta_seconds: frame.eta_seconds,
      started_at: frame.started_at,
      finished_at: frame.finished_at,
      error_message: frame.error_message,
    })
    if (!ACTIVE.has(frame.status)) {
      terminalTimer = setTimeout(() => {
        activeRun.value = null
        runState.value = 'empty'
      }, 3_000)
    }
  }

  async function reloadRun(): Promise<void> {
    if (!currentScope || !activeRun.value) return
    const run = await creativeAiApi.getLabellingRun(currentScope, activeRun.value.id)
    adoptRun(run)
  }

  function setRunError(error: ApiError): void {
    runError.value = error
    runState.value = 'error'
  }

  function resetState(): void {
    generation += 1
    if (terminalTimer) clearTimeout(terminalTimer)
    terminalTimer = null
    currentScope = null
    schemaRequest = null
    schema.value = null
    constraints.value = clone(FALLBACK_CONSTRAINTS)
    schemaState.value = 'empty'
    schemaError.value = null
    draft.value = []
    original.value = []
    labelErrors.value = {}
    saveState.value = 'empty'
    saveError.value = null
    suggestions.value = []
    suggestMeta.value = null
    suggestState.value = 'empty'
    suggestError.value = null
    activeRun.value = null
    runState.value = 'empty'
    runError.value = null
    nextLocalId = 1
  }

  return {
    schema,
    constraints,
    schemaState,
    schemaError,
    draft,
    original,
    labelErrors,
    saveState,
    saveError,
    lastSaveQueued,
    suggestions,
    suggestMeta,
    suggestState,
    suggestError,
    activeRun,
    runState,
    runError,
    diff,
    isDirty,
    hasActiveRun,
    runPercent,
    labelsByScope,
    labelCount,
    isAtCap,
    loadSchema,
    loadConstraints,
    resetDraft,
    upsertLabel,
    removeLabel,
    addSuggestions,
    saveSchema,
    loadSuggestions,
    adoptRun,
    applyRelabelProgress,
    reloadRun,
    setRunError,
    resetState,
    scope: () => currentScope,
  }
})

export type LabelSchemaStore = ReturnType<typeof useLabelSchemaStore>
